import type { LucideIcon } from "lucide-react";

import type { NamespaceId } from "@/types/namespaces";

export type WorkspaceTabIconId =
  | "sparkles"
  | "lightbulb"
  | "list-todo"
  | "file-text"
  | "folder"
  | "git-branch"
  | "git-compare"
  | "bot"
  | "workflow"
  | "palette"
  | "settings"
  | "alarm-clock"
  | "clipboard-check"
  | "layers"
  | "scale"
  | "shield-alert"
  | "target"
  | "map"
  | "waypoints"
  | "compass"
  | "cable"
  | "signpost";

export type WorkspaceTabIconValue = WorkspaceTabIconId | LucideIcon;

export type WorkspaceTab = {
  id: NamespaceId;
  label: string;
  icon?: WorkspaceTabIconValue;
  /**
   * Product feature key (FeatureCatalog) gating this workspace. When set, the
   * tab only renders when the feature resolves enabled for the active
   * namespace — a disabled feature's workspace is not displayed at all
   * (docs/feature-flags.md). Filtering happens in useAvailableWorkspaceTabs.
   */
  featureKey?: string;
};

export const DEFAULT_WORKSPACE_TABS: ReadonlyArray<WorkspaceTab> = [
  { id: "next", label: "Next", icon: "signpost" },
  { id: "ideas", label: "Ideas", icon: "lightbulb", featureKey: "ideas" },
  { id: "task-list", label: "Task list", icon: "list-todo" },
  { id: "goals", label: "Goals", icon: "target" },
  { id: "cron-jobs", label: "Cron jobs", icon: "alarm-clock" },
  { id: "documentation", label: "Documentation", icon: "file-text" },
  { id: "governance", label: "Governance", icon: "layers" },
  { id: "atlas", label: "Atlas", icon: "map" },
  { id: "flow", label: "Flow", icon: "waypoints" },
  { id: "guide", label: "Guide", icon: "compass" },
  { id: "integrations", label: "Integrations", icon: "cable" },
  { id: "plc", label: "PLC", icon: "workflow" },
  { id: "conventions", label: "Conventions", icon: "scale" },
  { id: "advisory", label: "Advisory", icon: "shield-alert" },
  { id: "files", label: "Files", icon: "folder" },
  { id: "repository", label: "Repository", icon: "git-branch" },
  { id: "changes", label: "Changes", icon: "git-compare", featureKey: "changes" },
  { id: "audit", label: "Audit", icon: "clipboard-check", featureKey: "audits" },
  { id: "settings", label: "Settings", icon: "settings" },
];

/**
 * Workspace tabs that start closed (absent from the rail) for users who haven't
 * customized their tabs. They remain listed in the top-bar workspace switcher
 * with a "Closed" badge and reopen on select, like any other closed tab.
 *
 * Empty by design: every workspace ships open and visible in the rail. Closing
 * a tab is a user decision, not a default we make for them. Users who have an
 * explicit saved tab preference keep it untouched.
 */
export const DEFAULT_CLOSED_WORKSPACE_TAB_IDS: NamespaceId[] = [];
